import { Cart } from '@/scripts/types/api'
import {
	qsOptional,
	qsRequired,
	qsRequiredFromDocument,
	trapFocus,
} from '@/scripts/core/global'
import { CartDrawerItems, LineItemCustom } from '@/scripts/cart/CartDrawerItems'
import { CartDrawer } from '@/scripts/cart/CartDrawer'

/************************************
 * Cart Update Functions
 ************************************/

type GwpAction =
	| {
			action: 'CONTINUE'
	  }
	| {
			action: 'ADD'
			variantId: number
	  }
	| {
			action: 'REMOVE'
			quantity: number
			variantId: number
			key: string
	  }

export type CartActionInput =
	| {
			type: 'line_update'
			lineItem: LineItemCustom
	  }
	| {
			type: 'line_add'
			lineItem: LineAddInput
	  }
	| {
			type: 'line_swap'
			lineItem: LineSwapInput
	  }

type LineAddInput = {
	quantity: number
	id: number
	selling_plan?: number
	price: number
}

type LineSwapInput = {
	quantity: number
	id: number
	selling_plan?: number
	price: number
	removedItemPrice: number
}

type LineAddPayload = Omit<LineAddInput, 'price'>

export function getGwpSettings() {
	const json = qsRequired(
		'[type="application/json"][data-uc-gwp-settings]'
	)?.textContent
	if (!json) {
		console.error('gwp settings not found')
		return {
			enabled: false,
			offerable: false,
			status: 'off',
			threshold: 0,
			gwpVariantId: 0,
			gwpType: 'threshold',
			requiredProducts: [],
		}
	}
	const settings = JSON.parse(json)
	const enabled = settings?.enabled === true ? true : false
	// only true while the gift is actually in stock - see cart-gwp-settings.liquid
	const offerable = settings?.offerable === true ? true : false
	const status: string = settings?.status ? settings.status : 'off'
	if (status === 'error') {
		console.error(
			'gwp disabled: the configured GWP Variant ID does not belong to the selected GWP Product'
		)
	}
	const threshold: number = settings?.threshold ? settings.threshold : 0
	const gwpVariantId: number = settings?.gwpVariantId
		? settings.gwpVariantId
		: 0
	const gwpType: 'threshold' | 'product_list' | 'bundle' =
		settings?.gwpType === 'product_list'
			? 'product_list'
			: settings?.gwpType === 'bundle'
				? 'bundle'
				: 'threshold'
	const requiredProducts: number[] = settings?.requiredProducts 
		? settings.requiredProducts 
		: []
	return {
		enabled,
		offerable,
		status,
		threshold,
		gwpVariantId,
		gwpType,
		requiredProducts,
	}
}

function getCartTotalAfterLineUpdate(
	lineItem: LineItemCustom,
	gwpOriginalPrice: number,
	linePercentDiscount: number
): number {
	const cart = getCartState()
	const quantityChange = lineItem.updateQuantity - lineItem.currentQuantity
	const cartLineTotal = cart.total_price
	const lineBeingUpdated = cart.items[lineItem.index - 1]
	const costOfLineBeingUpdated =
		lineBeingUpdated.final_price -
		lineBeingUpdated.final_price * linePercentDiscount * 0.01
	const changeInLineBeingUpdated = quantityChange * costOfLineBeingUpdated
	const cartPriceIncludingGwp = cartLineTotal - gwpOriginalPrice
	const cumulativePrice = cartPriceIncludingGwp + changeInLineBeingUpdated

	return cumulativePrice
}

function getCartTotalAfterLineAdd(
	lineItem: LineAddInput,
	gwpOriginalPrice: number,
	linePercentDiscount: number
): number {
	const cart = getCartState()
	const cartLineTotal = cart.total_price
	const cartPriceIncludingGwp = cartLineTotal - gwpOriginalPrice
	const cumulativePrice =
		cartPriceIncludingGwp +
		lineItem.price -
		lineItem.price * linePercentDiscount * 0.01
	return cumulativePrice
}

export function getGwpAction(input: CartActionInput): GwpAction {
	const action = resolveGwpAction(input)
	// an out-of-stock gift stops being handed out, but an existing gift line is
	// still allowed to be removed once the cart drops back below the threshold
	if (action.action === 'ADD' && !getGwpSettings().offerable) {
		return {
			action: 'CONTINUE',
		}
	}
	return action
}

function resolveGwpAction(input: CartActionInput): GwpAction {
	const cart = getCartState()
	const { enabled, threshold, gwpVariantId, gwpType, requiredProducts } = getGwpSettings()

	if (!enabled)
		return {
			action: 'CONTINUE',
		}

	const gwpItems = cart.items.filter(
		(item) => item.variant_id === gwpVariantId
	)

	const gwpQuantity =
		gwpItems.length > 0
			? gwpItems.reduce((acc, item) => acc + item.quantity, 0)
			: 0

	const gwpItem = gwpItems.length > 0 ? gwpItems[0] : undefined

	let gwpOriginalPrice = 0
	if (gwpItem) {
		gwpOriginalPrice = gwpItem.original_line_price * gwpQuantity
	}

	if (gwpType === 'threshold') {
		const cartLevelPercentageDiscountsCumulative =
			cart.cart_level_discount_applications
				.filter((application) => {
					return (
						application.value_type === 'percentage' &&
						application.target_selection === 'all' &&
						application.target_type === 'line_item'
					)
				})
				.map((application) =>
					typeof application.value === 'string'
						? parseInt(application.value)
						: application.value
				)
				.reduce((acc, value) => acc + value, 0)

		let cumulativePrice = 0
		switch (input.type) {
			case 'line_add':
				cumulativePrice = getCartTotalAfterLineAdd(
					input.lineItem,
					gwpOriginalPrice,
					cartLevelPercentageDiscountsCumulative
				)
				break
			case 'line_swap':
				cumulativePrice = getCartTotalAfterLineAdd(
					input.lineItem,
					gwpOriginalPrice,
					cartLevelPercentageDiscountsCumulative
				)
				cumulativePrice =
					cumulativePrice -
					input.lineItem.removedItemPrice +
					input.lineItem.removedItemPrice *
						cartLevelPercentageDiscountsCumulative *
						0.01
				break
			case 'line_update':
				cumulativePrice = getCartTotalAfterLineUpdate(
					input.lineItem,
					gwpOriginalPrice,
					cartLevelPercentageDiscountsCumulative
				)
				break
		}

		if (cumulativePrice >= threshold && gwpItem === undefined) {
			return {
				action: 'ADD',
				variantId: gwpVariantId,
			}
		} else if (cumulativePrice < threshold && gwpItem) {
			return {
				action: 'REMOVE',
				variantId: gwpVariantId,
				quantity: gwpItem.quantity - 1,
				key: gwpItem.key,
			}
		}
	} else if (gwpType === 'product_list') {
		// const hasRequiredVariant = cart.items.some(item =>
		// 	requiredProducts.includes(item.variant_id)
		// )

		const hasRequiredVariant = requiredProducts.includes(input.lineItem.id)

		console.log({hasRequiredVariant, items: cart.items, requiredProducts, input})

		if (hasRequiredVariant && gwpItem === undefined) {
			return {
				action: 'ADD',
				variantId: gwpVariantId,
			}
		} else if (!hasRequiredVariant && gwpItem) {
			return {
				action: 'REMOVE',
				variantId: gwpVariantId,
				quantity: gwpItem.quantity - 1,
				key: gwpItem.key,
			}
		}
	} else if (gwpType === 'bundle') {
		let hasBundleItem: boolean

		if (input.type === 'line_update' && input.lineItem.updateQuantity === 0 && input.lineItem.isBundle) {
			// When removing a bundle, exclude ALL items that share the same
			// _bundle ID since they are all being removed together
			const lineBeingRemoved = cart.items[input.lineItem.index - 1]
			const removedBundleId = lineBeingRemoved?.properties?._bundle
			if (removedBundleId) {
				hasBundleItem = cart.items.some(
					(item) =>
						item.variant_id !== gwpVariantId &&
						item.properties?._bundle &&
						item.properties._bundle !== removedBundleId
				)
			} else {
				hasBundleItem = cart.items.some(
					(item) => item.variant_id !== gwpVariantId && item.properties?._bundle
				)
			}
		} else {
			hasBundleItem = cart.items.some(
				(item) => item.variant_id !== gwpVariantId && item.properties?._bundle
			)
		}

		if (hasBundleItem && gwpItem === undefined) {
			return {
				action: 'ADD',
				variantId: gwpVariantId,
			}
		} else if (!hasBundleItem && gwpItem) {
			return {
				action: 'REMOVE',
				variantId: gwpVariantId,
				quantity: gwpItem.quantity - 1,
				key: gwpItem.key,
			}
		}
	}

	return {
		action: 'CONTINUE',
	}
}

export function getPayloadForLineUpdate(
	lineItem: LineItemCustom
): Record<number | string, number> {
	const updates: Record<string, number> = {}

	if (lineItem.isBundle && lineItem.bundleKeys) {
		const currentBundleQty = lineItem.currentQuantity || 1
		const newBundleQty = lineItem.updateQuantity
		lineItem.bundleKeys.forEach((key, i) => {
			const currentKeyQty = lineItem.bundleKeyQuantities?.[i] ?? currentBundleQty
			const perBundleQty = currentBundleQty > 0 ? currentKeyQty / currentBundleQty : 0
			updates[key] = Math.round(newBundleQty * perBundleQty)
		})
	} else {
		updates[`${lineItem.key}`] = lineItem.updateQuantity
	}

	if (lineItem.optionsGwpKeyToRemove && lineItem.updateQuantity === 0) {
		updates[lineItem.optionsGwpKeyToRemove] = 0
	}

	const gwpAction = getGwpAction({
		type: 'line_update',
		lineItem,
	})
	// a gift ADD is deliberately kept out of this payload. /cart/update.js is
	// all-or-nothing, so a sold-out gift would fail the customer's own quantity
	// change too. addGwpVariant() adds it separately once this request succeeds.
	if (gwpAction.action === 'REMOVE') {
		updates[gwpAction.key] = gwpAction.quantity
	}
	return updates
}

/**
 * The gift variant that should follow this cart action, or null. Read it
 * BEFORE sending the customer's own request - it is derived from the current
 * cart state plus the line being changed.
 */
export function getGwpVariantToAdd(input: CartActionInput): number | null {
	const gwpAction = getGwpAction(input)
	return gwpAction.action === 'ADD' ? gwpAction.variantId : null
}

/**
 * Adds the gift in its own request so that a gift which is sold out, or points
 * at a variant that no longer exists, can never fail the customer's own
 * add-to-cart. Returns fresh sections on success, undefined on failure.
 */
export async function addGwpVariant(
	variantId: number
): Promise<SectionApiResponse | undefined> {
	try {
		const response = await fetch(window.routes.cart_add_url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/javascript',
				'X-Requested-With': 'XMLHttpRequest',
			},
			body: JSON.stringify({
				items: [{ id: variantId, quantity: 1 }],
				sections: cartSectionsAsString(),
				sections_url: window.location.pathname,
			}),
		})
		const data = await response.json()
		if (data.status) {
			console.error('gwp add failed, customer cart left intact', data)
			return undefined
		}
		return data.sections
	} catch (e) {
		console.error('gwp add failed, customer cart left intact', e)
		return undefined
	}
}

export function checkBundleGwp(): { action: 'ADD' | 'NONE'; variantId: number } {
	const { enabled, offerable, gwpVariantId, gwpType } = getGwpSettings()
	if (!enabled || !offerable || gwpType !== 'bundle')
		return { action: 'NONE', variantId: 0 }

	const cart = getCartState()
	const gwpAlreadyInCart = cart.items.some(
		(item) => item.variant_id === gwpVariantId
	)
	const hasBundleItem = cart.items.some(
		(item) => item.variant_id !== gwpVariantId && item.properties?._bundle
	)

	if (hasBundleItem && !gwpAlreadyInCart) {
		return { action: 'ADD', variantId: gwpVariantId }
	}
	return { action: 'NONE', variantId: 0 }
}

// The gift is never bundled into these payloads - see addGwpVariant().
export function getPayloadForLineAdd(lineItem: LineAddInput): LineAddPayload[] {
	const linePayload: LineAddPayload = {
		quantity: lineItem.quantity,
		id: lineItem.id,
	}
	if (lineItem.selling_plan) {
		linePayload.selling_plan = lineItem.selling_plan
	}
	return [linePayload]
}

export function getPayloadForLineSwap(
	lineItem: LineAddInput,
	_removedItemPrice: number
): LineAddPayload[] {
	return getPayloadForLineAdd(lineItem)
}

/************************************
 * Cart Rendering Functions
 ************************************/

type SectionApiResponse = {
	'cart-drawer': string
	'cart-icon-bubble': string
	'cart-json': string
}

type SectionApiId = 'cart-drawer' | 'cart-icon-bubble' | 'cart-json'

export type ApplyUpdatesFromSectionAPIInput = {
	sections: SectionApiResponse
	exclude?: SectionApiId[]
	payload?: CartActionInput
}

export function applyUpdatesFromSectionApi(
	input: ApplyUpdatesFromSectionAPIInput
) {
	const { sections, exclude, payload } = input

	if (!exclude?.includes('cart-json') && sections['cart-json']) {
		updateCartDOMStateFromSectionAPI(sections['cart-json'])
	}
	if (!exclude?.includes('cart-drawer') && sections['cart-drawer']) {
		applyCartDrawerUpdate(sections['cart-drawer'], payload)
	}
	if (
		!exclude?.includes('cart-icon-bubble') &&
		sections['cart-icon-bubble']
	) {
		applyCartNotificationUpdate(sections['cart-icon-bubble'])
	}
}

function applyCartDrawerUpdate(html: string, payload?: CartActionInput) {
	const cart = getCartState()
	const isEmpty = cart.item_count === 0

	const cartDrawerItems = qsRequired<CartDrawerItems>('cart-drawer-items')
	const cartDrawer = qsRequired<CartDrawer>('cart-drawer')
	const cartFooter = qsOptional('#main-cart-footer')

	cartDrawerItems.classList.toggle('is-empty', isEmpty)

	cartFooter?.classList.toggle('is-empty', isEmpty)

	cartDrawer?.classList.toggle('is-empty', isEmpty)

	const oldCartDrawer = qsRequired(`#CartDrawer`)

	const newCartDrawerHTML = getSectionInnerHTML(html, '#CartDrawer')
	if (!newCartDrawerHTML) {
		console.error('applyCartDrawerUpdate dom parse failed')
		return
	}

	oldCartDrawer.innerHTML = newCartDrawerHTML

	cartDrawer.setOverlayListeners()

	if (isEmpty) {
		cartDrawer.close()
		return
	}

	if (
		payload?.type === 'line_update' &&
		qsOptional(`#CartDrawer-Item-${payload.lineItem.index}`)
	) {
		trapFocus(
			cartDrawer,
			qsRequired(`#CartDrawer-Item-${payload.lineItem.index}`)
		)
		return
	}

	// fallback: focus first cart item
	if (document.querySelector('.cart-item')) {
		trapFocus(cartDrawer, qsOptional('.cart-item__name'))
	}

	if (!isEmpty) {
		setTimeout(() => {
			cartDrawer.open()
		})
	}
}

function getSectionInnerHTML(html: string, selector: string) {
	const el = new DOMParser()
		.parseFromString(html, 'text/html')
		.querySelector(selector)
	if (!(el instanceof HTMLElement)) return undefined
	return el.innerHTML
}

function applyCartNotificationUpdate(html: string) {
	const elToReplace = qsRequired('#cart-icon-bubble')
	const newHTML = getSectionInnerHTML(html, '.shopify-section')
	if (!newHTML) {
		console.error('applyCartNotificationUpdate dom parse failed')
		return
	}
	elToReplace.innerHTML = newHTML
}

export function cartSectionsToRender() {
	return [
		{
			section: 'cart-drawer',
			selector: '#CartDrawer',
		},
		{
			section: 'cart-icon-bubble',
			selector: '.shopify-section',
		},
		{
			section: 'cart-json',
			selector: '.shopify-section',
		},
	]
}

export function cartSectionsAsString(): string {
	return cartSectionsToRender()
		.map((s) => s.section)
		.join(',')
}

export function getCartState(): Cart {
	const json: string | null = qsRequired<HTMLScriptElement>(
		'script[type="application/json"][data-uc-cart-json]'
	).textContent
	if (!json) throw new Error('No cart JSON')
	return JSON.parse(json)
}

export function updateCartDOMStateFromSectionAPI(html: string) {
	const domParser = new DOMParser().parseFromString(html, 'text/html')
	const newJsonEl = qsRequiredFromDocument<HTMLScriptElement>(
		'script[type="application/json"][data-uc-cart-json]',
		domParser
	)
	const oldJsonEl = qsRequired(
		'script[type="application/json"][data-uc-cart-json]'
	)
	oldJsonEl.innerHTML = newJsonEl.innerHTML
}

export function refreshCartStateFromSectionAPI(html: string): Cart {
	updateCartDOMStateFromSectionAPI(html)
	return getCartState()
}